// Single source of truth for dates on the site.
// Storage/transport = ISO "YYYY-MM-DD". Display to users = DD/MM/YYYY (Western digits 0-9).
export const pad2 = (n) => String(n).padStart(2, "0");

// Keep digits only (max 8) and insert slashes while typing: 13031989 -> 13/03/1989
export const maskDigits = (raw) => {
  const d = String(raw ?? "").replace(/\D/g, "").slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join("/");
};

// Real-date check (rejects 31/02/2020, accepts 29/02/2024, rejects 29/02/2023)
export const isValidDMY = (dd, mm, yyyy) => {
  const D = +dd, M = +mm, Y = +yyyy;
  if (!(Number.isInteger(Y) && Y >= 1000 && M >= 1 && M <= 12 && D >= 1)) return false;
  const lastDay = new Date(Y, M, 0).getDate();
  return D <= lastDay;
};

// "13/03/1989" (or raw 8 digits) -> "1989-03-13"; invalid/incomplete -> null
export const isoFromDisplay = (display) => {
  const d = String(display ?? "").replace(/\D/g, "");
  if (d.length !== 8) return null;
  const dd = d.slice(0, 2), mm = d.slice(2, 4), yyyy = d.slice(4, 8);
  if (!isValidDMY(dd, mm, yyyy)) return null;
  return `${yyyy}-${mm}-${dd}`;
};

// ISO "1989-03-13" -> "13/03/1989"; also tolerates legacy DD-MM-YYYY / DDMMYYYY text
export const displayFromIso = (value) => {
  if (!value) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = String(value).replace(/\D/g, "");
  if (d.length === 8) return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4, 8)}`;
  return "";
};

// THE display formatter. Any stored date -> DD/MM/YYYY (empty string when unknown).
export const formatDate = (value) => {
  if (!value) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const digits = String(value).replace(/\D/g, "");
  if (digits.length === 8 && /[/-]/.test(String(value))) {
    // DD/MM/YYYY or DD-MM-YYYY already
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  }
  const dt = new Date(value);
  if (!isNaN(dt.getTime())) return `${pad2(dt.getUTCDate())}/${pad2(dt.getUTCMonth() + 1)}/${dt.getUTCFullYear()}`;
  return "";
};

// Timestamp -> "DD/MM/YYYY HH:MM" in 24-hour IST.
export const formatDateTime = (value) => {
  if (!value) return "";
  const dt = new Date(value);
  if (isNaN(dt.getTime())) return formatDate(value);
  const ist = new Date(dt.getTime() + (5 * 60 + 30) * 60000); // UTC -> IST
  return `${pad2(ist.getUTCDate())}/${pad2(ist.getUTCMonth() + 1)}/${ist.getUTCFullYear()} ${pad2(ist.getUTCHours())}:${pad2(ist.getUTCMinutes())}`;
};

// 24h HH:MM validation for TimeField
export const isValidTime = (hhmm) => {
  const m = /^(\d{2}):(\d{2})$/.exec(String(hhmm || ""));
  if (!m) return false;
  return +m[1] <= 23 && +m[2] <= 59;
};
export const maskTime = (raw) => {
  const d = String(raw ?? "").replace(/\D/g, "").slice(0, 4);
  return [d.slice(0, 2), d.slice(2, 4)].filter(Boolean).join(":");
};
